<script lang="ts" setup>
import AUApi from "@/api";
import { reactive, onMounted, nextTick, ref } from "vue";
import { RenderDayCellEventArgs } from "@syncfusion/ej2-calendars";
import { globalsBezeichnung, begriffBezeichnung } from "@/local";
import { Globals } from "@/Globals";

import {
  getDateFromInput,
  getMask,
  highlightDays,
  valueToFormatStr,
  getHolidayColorVuetify,
} from "./CalenderHelper";
import { findSperrDate } from "@/SperreHelper";

import {
  CommonEditControlEmits,
  EditControlProps,
  getDefaultEditorControlConfig,
  mounted,
  valueChanged,
} from "@/editor/EditComponentStatic";
import MaskedInput from "./MaskedInput.vue";
import { TimeEntry } from "./EditTimePicker.vue";
import { useRegisterValidation } from "@/components/EditorValidator";

declare interface EditDateRangeControlProps extends EditControlProps {
  sperrBegriff?: string;
  showGanztag?: boolean;
}
declare interface EditDateRangeUiData extends EditorControlUiData {
  von?: Date;
  bis?: Date;
  pickerDates: any[]; // = [];
  showPicker: boolean; // = false;

  vonStr: string; // = ""; // String Anzeige
  bisStr: string; // = "";

  minDate?: string;

  vonError: string; // = "";
  bisError: string; // = "";

  IsAllDay: boolean; // = true;
  allDayVisible: boolean; // = true;
  showFields: boolean; // = true;
  mask: string; // = "D1.M1.1111";

  calendarVal: string; // = "";
  dateFormat: string; // = "dd-MM-yyyy";
  format: string; // = "dd.MM.yyyy";

  allDayFormat: string; // = "dd.MM.yyyy";
  notAllDayFormat: string; // = "HH:mm dd.MM.yyyy";

  pickertitle: string; // = "Wählen sie ein Start-Datum:";
  selectinVon: boolean; // = true;
  changing: boolean; //= false;

  ignoreNextPickerEvents: number; // = 2;
  iconCss: string; // = "";
  showTooltip: boolean; // = false;
  tooltipText: string; // = "";
}

const props = defineProps<EditDateRangeControlProps>();
const emitFn = defineEmits<CommonEditControlEmits>();

const { config, ui } = getDefaultEditorControlConfig<
  EditorControlConfig,
  EditDateRangeControlProps,
  EditDateRangeUiData
>(props, emitFn, {
  // default start values
  pickerDates: [],
  showPicker: false,

  vonStr: "", // String Anzeige
  bisStr: "",

  vonError: "",
  bisError: "",

  IsAllDay: true,
  allDayVisible: true,
  showFields: true,
  mask: "D1.M1.1111",

  calendarVal: "",
  dateFormat: "dd-MM-yyyy",
  format: "dd.MM.yyyy",

  allDayFormat: "dd.MM.yyyy",
  notAllDayFormat: "HH:mm dd.MM.yyyy",

  pickertitle: "Wählen sie ein Start-Datum:",
  selectinVon: true,
  changing: false,

  ignoreNextPickerEvents: 2,
  iconCss: "",
  showTooltip: false,
  tooltipText: "",
});

config.validate = validate;
useRegisterValidation({ config });

const uiData = reactive(ui);
interface PickerValues {
  entry?: TimeEntry;
  buttonText: string;
  selectedText: string;
  showPicker: boolean;
  left: number;
  top: number;
}
let pickerVon: PickerValues = {
  showPicker: false,
  buttonText: "---",
  selectedText: "00:00",
  left: 0,
  top: 0,
};
let pickerBis: PickerValues = {
  showPicker: false,
  buttonText: "---",
  selectedText: "23:30",
  left: 0,
  top: 0,
};
let initalized = ref<boolean>(false);
onMounted(async () => {
  initalized.value = false;
  mounted(uiData, props, config);
  await mountedDateRange();
});
function getFromTimeDate() {
  return uiData.pickerDates.length > 0 ? uiData.pickerDates[0] : new Date();
}
function getToTimeDate() {
  return uiData.pickerDates.length > 1 ? uiData.pickerDates[1] : new Date();
}

/* presets */
// const weekStart: any = new Date(
//   new Date(
//     new Date().setDate(new Date().getDate() - ((new Date().getDay() + 7) % 7))
//   ).toDateString()
// );
// const weekEnd: any = new Date(
//   new Date(
//     new Date().setDate(
//       new Date(
//         new Date().setDate(
//           new Date().getDate() - ((new Date().getDay() + 7) % 7)
//         )
//       ).getDate() + 6
//     )
//   ).toDateString()
// );

function selectedDatesText() {
  if (uiData.pickerDates.length > 1) {
    const args = uiData.pickerDates;
    let bis = new Date(args[1]);
    let von = new Date(args[0]);
    if (von && bis && von > bis) {
      const x = von;
      von = bis;
      bis = x;
    }
    const diff = von.diffInDays(bis);
    const text = begriffBezeichnung("Tage");
    return diff + " " + text;
  }
  return "--";
}
async function mountedDateRange() {
  uiData.changing = true;
  if (!props.showGanztag) {
    uiData.allDayVisible = false;
    uiData.notAllDayFormat = "HH:mm";
  }

  pickerVon = {
    showPicker: false,
    buttonText: "---",
    selectedText: "00:00",
    left: 0,
    top: 0,
  };
  pickerBis = {
    showPicker: false,
    buttonText: "---",
    selectedText: "23:30",
    left: 0,
    top: 0,
  };
  // default wert setzen...
  uiData.minDate = "1970-01-01";
  if (props.sperrBegriff) {
    const sperrDate = getSperrDate(props.sperrBegriff);
    if (sperrDate) {
      uiData.minDate = new Date(sperrDate).toISOString().split("T")[0];
    }
  }
  // im data steht es richtig drinenen - auch wenn NEU Zustand ist!
  if (uiData.data && !uiData.data?.IsAllDay) {
    uiData.IsAllDay = false;
  }

  if (uiData.IsAllDay) {
    uiData.format = uiData.allDayFormat;
  } else {
    uiData.format = uiData.notAllDayFormat;
  }

  uiData.mask = getMask(uiData.format);
  const val = config.initValue;

  if (val) {
    if (val.IsAllDay === true) {
      if (val.von) {
        val.von = new Date(
          new Date(new Date(val.von).setHours(0)).setMinutes(0),
        ).toISOStringWithTZGMT();
      }
      if (val.bis) {
        val.bis = new Date(
          new Date(new Date(val.bis).setHours(0)).setMinutes(0),
        ).toISOStringWithTZGMT();
      }
    }
    if (val.von) {
      uiData.von = new Date(val.von);
      uiData.vonStr = valueToFormatStr(uiData.von, uiData.format);

      if (!uiData.IsAllDay) {
        pickerVon.buttonText = pickerVon.selectedText = valueToFormatStr(
          uiData.von,
          "HH:mm",
        );
      }
    }
    if (val.bis) {
      uiData.bis = new Date(val.bis);
      let bisDisplay;
      if (val.von !== val.bis) {
        if (uiData.IsAllDay) {
          bisDisplay = uiData.bis = new Date(val.bis).addDays(-1);
        } else {
          bisDisplay = new Date(val.bis);
        }
      } else {
        bisDisplay = uiData.bis;
      }
      uiData.bisStr = valueToFormatStr(bisDisplay, uiData.format);

      uiData.bisStr = valueToFormatStr(bisDisplay, uiData.format);
    }
  }

  await setPickerDates();

  uiData.ignoreNextPickerEvents = 2;
  nextTick(() => ((uiData.changing = false), (initalized.value = true)));
}

function toggleTimePickerButton(fuerVon: boolean) {
  const entry = fuerVon ? pickerVon : pickerBis;
  // const btn: Vue = (
  //   fuerVon ? refs.timePickerButtonVon : refs.timePickerButtonBis
  // ) as Vue;

  entry.showPicker = !entry.showPicker;
  // try {
  //   const rect = (btn.$el as HTMLElement).getBoundingClientRect();

  //   entry.left = rect.left;
  //   if (rect.width + rect.width > window.innerWidth) {
  //     entry.left -= rect.width;
  //   }
  //   entry.top = rect.top;
  // } catch (e) {
  //   console.error(e);
  // }
}

function setPickedTimeVon(item: any) {
  const date = new Date(item);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const formattedTime = `${String(hours).padStart(2, "0")}:${String(
    minutes,
  ).padStart(2, "0")}`;
  pickerVon.entry = item;
  pickerVon.buttonText = formattedTime;
  pickerVon.showPicker = false;
}
function setPickedTimeBis(item: any) {
  const date = new Date(item);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const formattedTime = `${String(hours).padStart(2, "0")}:${String(
    minutes,
  ).padStart(2, "0")}`;
  pickerBis.entry = item;
  pickerBis.buttonText = formattedTime;
  pickerBis.showPicker = false;
}

async function setPickerDates(removeFromBis: boolean = false) {
  if (uiData.von && uiData.bis) {
    const bis = removeFromBis ? uiData.bis.addDays(-1) : uiData.bis;
    uiData.pickerDates = [
      uiData.von.toISOStringWithTZ().substr(0, 10),
      bis.toISOStringWithTZ().substr(0, 10),
    ];
  } else if (uiData.von) {
    uiData.pickerDates = [
      uiData.von.toISOStringWithTZ().substr(0, 10),
      uiData.von.toISOStringWithTZ().substr(0, 10),
    ];
  }
  if (!uiData.IsAllDay) {
    if (uiData.von) {
      const time = valueToFormatStr(uiData.von, "HH:mm");
      pickerVon.buttonText = pickerVon.selectedText = time;
    }
    if (uiData.bis) {
      let time = valueToFormatStr(uiData.bis, "HH:mm");
      if (time === "00:00") {
        time = "23:30";
      }

      pickerBis.buttonText = pickerBis.selectedText = time;
    }
  }
}

// hier berechnen wir die Zeit korrekt mit dem DateTime Offset vom dem Datum wo wir losstarten
function calcDateTimeForTargetTimeZone(date: Date, delta: number) {
  const dateWithoutTZ = new Date(
    date.toISOStringWithTZ().substr(0, 10) + "T00:00",
  );
  return dateWithoutTZ.addMinutes(delta);
}

function closePicker(saveValue: boolean) {
  pickerVon.showPicker = pickerBis.showPicker = uiData.showPicker = false;
  if (saveValue) {
    const von =
      uiData.pickerDates.length > 0
        ? new Date(uiData.pickerDates[0])
        : undefined;

    let bis =
      uiData.pickerDates.length > 1
        ? new Date(uiData.pickerDates.at(-1))
        : undefined;
    // ist von und bis ident. dann übergibt der Picker nur 1 datum.. dann muss ich es künstlich setzen
    if (!bis) {
      bis = von;
    }
    setVonBis(von, bis);
  }
}

function allDayChanged(args: boolean) {
  try {
    // console.log("allDayChanged " + JSON.stringify(args));
    const von = getDateFromInput(uiData.vonStr, uiData.format);
    const bis = getDateFromInput(uiData.bisStr, uiData.format);

    uiData.showFields = false;
    uiData.IsAllDay = args;
    if (uiData.IsAllDay) {
      uiData.format = uiData.allDayFormat;
    } else {
      uiData.format = uiData.notAllDayFormat;
    }
    uiData.mask = getMask(uiData.format);
    uiData.vonStr = "";
    uiData.bisStr = "";
    nextTick(() => {
      uiData.showFields = true;
      if (
        von.date &&
        von.date.toISOStringWithTZ().substr(0, 16) !== "1970-01-01T00:00"
      ) {
        if (uiData.IsAllDay) {
          // wurde von Stunden wieder auf TAG umgestellt,
          // dann muss ich die Stunden rausschmeissen & auch ValueChanged setzen!
          von.date = new Date(
            new Date(new Date(von.date).setHours(0)).setMinutes(0),
          );

          bis.date = new Date(
            new Date(new Date(von.date).setHours(0)).setMinutes(0),
          );
        }
        uiData.vonStr = valueToFormatStr(von.date, uiData.format);
      }
      if (
        bis.date &&
        bis.date.toISOStringWithTZ().substr(0, 16) !== "1970-01-01T00:00"
      ) {
        uiData.bisStr = valueToFormatStr(bis.date, uiData.format);
      }
    });
    rangeChanged();
  } catch (e) {
    console.error(e);
  }
}

function isTimeRelatedFormat(format: string): boolean {
  return (
    format.includes("HH") &&
    !format.includes("MM") &&
    !format.includes("yyyy") &&
    !format.includes("yy")
  );
}

function vonChanged(x: any, fire: boolean = true) {
  console.log("von...");
  try {
    const val = getDateFromInput(x, uiData.format);
    // console.log(x + " -> " + val);
    if (val) {
      if (val.date) {
        uiData.von = val.date;
        if (uiData.minDate && !isTimeRelatedFormat(uiData.format)) {
          const minDateDate = new Date(uiData.minDate);
          if (!uiData.von || uiData.von < minDateDate) {
            const minDateStr = minDateDate.toISOStringWithTZ().substring(0, 10);
            val.error = "ab" + " " + minDateStr;
          }
        }
      }
      if (val.error) {
        uiData.vonError = val.error;
        fire = false; // nicht event feuern weil ungültig...
      } else {
        uiData.vonError = "";
      }
    }
    if (fire) {
      rangeChanged();
    }
  } catch (e) {
    console.error(e);
  }
}
function bisChanged(x: any, fire: boolean = true) {
  console.log("bis...");
  try {
    const val = getDateFromInput(x, uiData.format);

    // console.log(x + " -> " + val);
    if (val) {
      if (val.date) {
        uiData.bis = val.date;
      }
      if (val.error) {
        uiData.bisError = val.error;
        fire = false; // nicht event feuern weil ungültig...
      } else {
        uiData.bisError = "";
      }
    }

    if (fire) {
      rangeChanged();
    }
  } catch (e) {
    console.error(e);
  }
}

function validate(): boolean {
  try {
    if (
      (uiData.vonError && uiData.vonError.length > 0) ||
      (uiData.bisError && uiData.bisError.length > 0)
    ) {
      return false;
    }
    return true;
  } catch (error) {
    console.log(error);
  }
  return false;
}

async function rangeChanged() {
  let von = null;
  let bis = null;
  if (uiData.von) {
    von = uiData.von.toISOStringWithTZ();
  }
  // if (!IsAllDay && von) {
  //   // bis = von;
  //   bis = von.toISOStringWithTZ();
  //   vonPicker = von;
  //   // bisPicker = von;
  // }
  if (uiData.bis) {
    bis = uiData.bis.toISOStringWithTZ();
  }
  if (uiData.von && uiData.bis && uiData.von > uiData.bis) {
    // Bei reinem Zeit-Format (HH:mm ohne Monat/Jahr) über Mitternacht erlauben:
    // wenn Bis-Uhrzeit < Von-Uhrzeit -> Bis auf nächsten Tag setzen
    if (isTimeRelatedFormat(uiData.format)) {
      uiData.bis = new Date(uiData.bis);
      uiData.bis.setDate(uiData.bis.getDate() + 1);
      bis = uiData.bis.toISOStringWithTZ();
      uiData.bisError = "";
    } else {
      uiData.bisError = "von > bis !";
      // ups abbrechen
      return;
    }
  } else {
    uiData.bisError = "";
  }

  await setPickerDates();

  const val = {
    von,
    bis,
    IsAllDay: uiData.IsAllDay,
  };
  // 13.7.2020 das darf ich nicht machen - sonst hab ich bei MANUELLER eingabe einen Tag zu viel!
  // if (IsAllDay && bis) {
  //   val.bis = new Date(bis).addDays(1).toISOStringWithTZ();
  // }

  console.log(JSON.stringify(val));
  valueChangedRange(val);
}

function valueChangedRange(val: any) {
  if (val && val.IsAllDay) {
    if (val.von) {
      val.von = new Date(val.von).asLocalDateString();
    }
    if (val.bis) {
      val.bis = new Date(val.bis).addDays(1);
      val.bis = new Date(val.bis).asLocalDateString();
    }
  }
  valueChanged(config, uiData, val);
}

function setVonBis(von?: Date, bis?: Date) {
  uiData.showFields = false;
  if (von && bis && von > bis) {
    const x = von;
    von = bis;
    bis = x;
  }

  let isodate = "";
  if (von) {
    isodate += von.toISOStringWithTZ().substring(0, 10); // nur das datum ;)
  }
  if (bis) {
    isodate += bis.toISOStringWithTZ().substring(0, 10);
  }

  // falls vorhanden Uhrzeit und von geschriebenem übertragen
  if (von && uiData.IsAllDay) {
    if (uiData.von) {
      von.setHours(uiData.von.getHours());
      von.setMinutes(uiData.von.getMinutes());
    }
  }
  if (bis && uiData.IsAllDay) {
    if (uiData.bis) {
      bis.setHours(uiData.bis.getHours());
      bis.setMinutes(uiData.bis.getMinutes());
    }
  }

  let isodateval = "";
  if (von) {
    isodateval += von.toISOStringWithTZ();
  }
  if (bis) {
    isodateval += bis.toISOStringWithTZ();
  }
  //    showFields = false;
  if (isodate !== isodateval) {
    uiData.vonStr = von ? valueToFormatStr(von, uiData.format) : "";
    uiData.von = von;
    uiData.bisStr = bis ? valueToFormatStr(bis, uiData.format) : "";
    uiData.bis = bis;
  }

  nextTick(() => {
    uiData.showFields = true;
    if (uiData.von && uiData.bis) {
      valueChangedRange({
        von: uiData.von.toISOStringWithTZ(),
        bis: uiData.bis.toISOStringWithTZ(),
        IsAllDay: uiData.IsAllDay,
      });
    } else if (uiData.von) {
      valueChangedRange({
        von: uiData.von.toISOStringWithTZ(),
        bis: null,
        IsAllDay: uiData.IsAllDay,
      });
    } else if (uiData.bis) {
      valueChangedRange(null);
    }
    uiData.changing = false; // muss zurück gesetzt werden! sonst geht keine Änderung über den DatePicker mehr!
  });
}

function getHolidayColorVuetifyUI(args: string) {
  try {
    return getHolidayColorVuetify(new Date(args)) ?? "";
  } catch (err) {
    console.error(err);
  }
}
function onDayHover(day: string) {
  const feiertag = AUApi.Current.getHoliday(day);
  if (feiertag) {
    uiData.showTooltip = true;
    uiData.tooltipText = feiertag.bezeichnung;
  }
}
function onDayLeave(args: any) {
  uiData.showTooltip = false;
}

function getSperrDate(begriff: any) {
  const aic = AUApi.Current.user.aic;
  if (props.query || begriff.kennung) {
    const t = findSperrDate(props.query || begriff.kennung, aic);
    if (t) {
      const sperrDate = t.sperrDate;
      return new Date(sperrDate);
    }
  }
}
</script>
<template>
  <v-container v-if="uiData.showMutable" class="pa-0">
    <v-row align-start class="pa-0">
      <v-col :cols="uiData.IsAllDay ? 5 : 6" class="fullcol">
        <MaskedInput
          :disabled="uiData.disabled"
          v-if="uiData.showFields"
          ref="von"
          :label="globalsBezeichnung(Globals.Begriff, 'von', 'von')"
          v-model="uiData.vonStr"
          :errorText="uiData.vonError"
          :mask="uiData.mask"
          @update:modelValue="vonChanged"
        />
        <ValueChangeTracker
          v-if="uiData.isChanged || props.disableValueChangeTracker"
        />
      </v-col>
      <v-col :cols="uiData.IsAllDay ? 5 : 6" class="fullcol">
        <MaskedInput
          :disabled="uiData.disabled"
          v-if="uiData.showFields"
          ref="bis"
          :label="globalsBezeichnung(Globals.Begriff, 'bis', 'bis')"
          v-model="uiData.bisStr"
          :errorText="uiData.bisError"
          :mask="uiData.mask"
          @update:modelValue="bisChanged"
        />
        <ValueChangeTracker
          v-if="uiData.isChanged || props.disableValueChangeTracker"
        />
      </v-col>
      <v-col
        cols="1"
        class="fullcol2 d-flex align-end justify-center pb-6"
        v-if="uiData.IsAllDay"
      >
        <v-menu
          :close-on-click="true"
          :close-on-content-click="false"
          :offset-y="true"
          v-model="uiData.showPicker"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-if="!uiData.disabled"
              variant="outlined"
              v-bind="props"
              color="primary"
            >
              <v-icon>mdi-calendar</v-icon>
            </v-btn>
          </template>
          <v-card
            :style="
              !uiData.IsAllDay
                ? 'background: white; min-width: 480px;'
                : 'background: white;'
            "
          >
            <v-tooltip
              :disabled="!uiData.showtooltip"
              location="top"
              v-model="uiData.showTooltip"
              nudge-top="-55"
              max-width="300px"
              :text="uiData.tooltipText"
            >
              <template #activator="{ props }">
                <div v-bind="props">
                  <v-date-picker
                    v-model="uiData.pickerDates"
                    locale="de-de"
                    :min="uiData.minDate"
                    multiple="range"
                    first-day-of-week="1"
                    :show-week="true"
                    :selected-items-text="selectedDatesText"
                    @mouseover:date="onDayHover"
                    @mouseleave:date="onDayLeave"
                  ></v-date-picker>
                </div>
              </template>
            </v-tooltip>

            <v-spacer></v-spacer>
            <div
              style="
                text-align: start;
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
                padding: 4px 8px;
              "
              v-if="!uiData.IsAllDay"
            >
              <v-btn
                @click="toggleTimePickerButton(true)"
                width="49%"
                inline
                ref="timePickerButtonVon"
                >von {{ pickerVon.buttonText }}</v-btn
              >
              <v-btn
                @click="toggleTimePickerButton(false)"
                width="49%"
                ref="timePickerButtonBis"
                >bis {{ pickerBis.buttonText }}</v-btn
              >

              <EditTimePicker
                :showPicker="pickerVon.showPicker"
                :menuLeft="pickerVon.left"
                :menuTop="pickerVon.top"
                @change="setPickedTimeVon"
                :selectedText="pickerVon.selectedText"
                :date="getToTimeDate()"
                style="flex: 1 1 200px"
              />
              <EditTimePicker
                :showPicker="pickerBis.showPicker"
                :menuLeft="pickerBis.left"
                :menuTop="pickerBis.top"
                @change="setPickedTimeBis"
                :selectedText="pickerBis.selectedText"
                :date="getFromTimeDate()"
                style="flex: 1 1 200px"
              />
            </div>
            <v-spacer></v-spacer>
            <div style="text-align: right">
              <v-btn @click="closePicker(false)" variant="text" color="primary"
                >{{ begriffBezeichnung("Close") }}
              </v-btn>
              <v-btn color="success" variant="text" @click="closePicker(true)"
                >Ok
              </v-btn>
            </div>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>

    <v-row class="mt-0 pt-0" v-if="props.showGanztag">
      <v-col cols="12" align-self="center" class="fullcol">
        <v-checkbox
          :disabled="uiData.disabled"
          v-model="uiData.IsAllDay"
          :label="
            globalsBezeichnung(Globals.Begriff, 'Web_ganztaegig', 'ganztägig')
          "
          @update:modelValue="allDayChanged(uiData.IsAllDay)"
          hide-details
        ></v-checkbox>
      </v-col>
    </v-row>
  </v-container>
</template>
<style scoped>
.fullcol {
  width: 100%;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 0px;
  padding-right: 5px;
}
.fullcol2 {
  width: 100%;
  padding-top: 0px;
  padding-left: 0px;
  padding-right: 0px;
}

.rotateIcon {
  /* transform: rotate(90deg); */
  color: green !important;
  /* color: red; */
}
.rangeDateHighlight {
  border-top: 3px solid #4f4 !important;
  background-color: #cfc !important;
}
</style>
